/**
 * Browser voice channel — thin fetch wrapper for the voice-agent turn routes.
 *
 * Uses raw fetch (not the shared ApiClient) because /voice/turn answers with a
 * streamed audio/mpeg body plus metadata in custom headers, which the
 * JSON-oriented client can't express. Auth is the same HS256 session JWT the
 * rest of the app sends.
 *
 * /voice/turn 503s when ElevenLabs isn't configured (local dev without the
 * secret) — we fall back to /text/turn so the conversation still works,
 * just without audio.
 */

import { loadSession } from "../auth/session";

const baseUrl =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  window.location.origin;

/** Content blocks mirror the server's HistoryMessage — text blocks are all we
 * construct, but /text/turn's canonical history can thread tool_use /
 * tool_result blocks through, so the type stays permissive. */
export interface VoiceHistoryMessage {
  role: "user" | "assistant";
  content: ({ type: string } & Record<string, unknown>)[];
}

export interface VoiceTurnResult {
  text: string;
  stopReason: string;
  /** Object URL for the synthesized MP3 — null when TTS is unavailable. */
  audioUrl: string | null;
  history: VoiceHistoryMessage[];
  /** Durable conversation id (V045) — thread it into the next turn. Null when
   * persistence was unavailable server-side. */
  conversationId: string | null;
}

export interface ConversationSummary {
  id: string;
  title: string;
  started_at: string;
  last_activity_at: string;
  turn_count: number;
}

export interface ConversationTurn {
  role: "user" | "assistant";
  text: string;
  tool_names: string[];
  created_at: string;
}

export interface ConversationDetail extends ConversationSummary {
  turns: ConversationTurn[];
}

export interface FollowupRequest {
  id: string;
  topic: string;
  details: string;
  preferred_time: string | null;
  status: "NEW" | "CONTACTED" | "CLOSED";
  agent_reply: string | null;
  replied_at: string | null;
  created_at: string;
  contacted_at: string | null;
  closed_at: string | null;
}

export class VoiceApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "VoiceApiError";
  }
}

function authHeaders(): Record<string, string> {
  const session = loadSession();
  if (!session) throw new VoiceApiError("Not signed in", 401);
  return {
    Authorization: `Bearer ${session.accessToken}`,
    "Content-Type": "application/json",
  };
}

function decodeB64Utf8(value: string): string {
  const bytes = Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function appendedHistory(
  history: VoiceHistoryMessage[],
  userText: string,
  assistantText: string,
): VoiceHistoryMessage[] {
  const next: VoiceHistoryMessage[] = [
    ...history,
    { role: "user", content: [{ type: "text", text: userText }] },
  ];
  // Bedrock rejects empty text blocks — a voice reply that came back textless
  // must not poison the rest of the conversation (Checkpoint #9 finding; the
  // backend filters too, this is belt-and-braces).
  if (assistantText.trim()) {
    next.push({
      role: "assistant",
      content: [{ type: "text", text: assistantText }],
    });
  }
  return next;
}

export async function voiceTurn(
  message: string,
  history: VoiceHistoryMessage[],
  conversationId: string | null,
  signal?: AbortSignal,
): Promise<VoiceTurnResult> {
  const body = JSON.stringify({
    message,
    history,
    conversation_id: conversationId,
  });
  const response = await fetch(`${baseUrl}/voice-agent/voice/turn`, {
    method: "POST",
    headers: authHeaders(),
    body,
    signal,
  });

  // 503 = TTS not configured (e.g. local dev without the ElevenLabs secret).
  // Degrade to the text route rather than failing the conversation.
  if (response.status === 503)
    return textTurn(message, history, conversationId, signal);
  if (!response.ok) {
    throw new VoiceApiError(
      `voice turn failed (HTTP ${response.status})`,
      response.status,
    );
  }

  const textB64 = response.headers.get("X-Agent-Text-B64");
  const text = textB64 ? decodeB64Utf8(textB64) : "";
  const audioBlob = await response.blob();
  return {
    text,
    stopReason: response.headers.get("X-Agent-Stop-Reason") ?? "end_turn",
    audioUrl: audioBlob.size > 0 ? URL.createObjectURL(audioBlob) : null,
    // /voice/turn carries no history in its headers — thread the final text
    // only (tool_use blocks are dropped; fine for conversational context).
    history: appendedHistory(history, message, text),
    conversationId: response.headers.get("X-Conversation-Id") ?? conversationId,
  };
}

async function textTurn(
  message: string,
  history: VoiceHistoryMessage[],
  conversationId: string | null,
  signal?: AbortSignal,
): Promise<VoiceTurnResult> {
  const response = await fetch(`${baseUrl}/voice-agent/text/turn`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ message, history, conversation_id: conversationId }),
    signal,
  });
  if (!response.ok) {
    throw new VoiceApiError(
      `text turn failed (HTTP ${response.status})`,
      response.status,
    );
  }
  const payload = (await response.json()) as {
    text: string;
    stop_reason: string;
    history?: VoiceHistoryMessage[];
    conversation_id?: string | null;
  };
  return {
    text: payload.text,
    stopReason: payload.stop_reason,
    audioUrl: null,
    // /text/turn returns the canonical updated history — prefer it.
    history: payload.history ?? appendedHistory(history, message, payload.text),
    conversationId: payload.conversation_id ?? conversationId,
  };
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) {
    throw new VoiceApiError(
      `request failed (HTTP ${response.status})`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/** The caller's past conversations, newest first (V045). */
export function listConversations(
  signal?: AbortSignal,
): Promise<ConversationSummary[]> {
  return getJson("/voice-agent/conversations", signal);
}

/** One conversation with its full transcript. */
export function getConversation(
  id: string,
  signal?: AbortSignal,
): Promise<ConversationDetail> {
  return getJson(`/voice-agent/conversations/${id}`, signal);
}

/** The caller's followup requests + where each one stands ("My requests"). */
export function listMyFollowups(
  signal?: AbortSignal,
): Promise<FollowupRequest[]> {
  return getJson("/voice-agent/followups", signal);
}
